import { type AuthenticationMethod } from "@auth0/auth0-react";

/**
 * Factor types Auth0 auto-attaches to accounts that are not user-managed MFA methods. They are
 * hidden from the management list AND excluded from the last-factor guard, so they never block
 * removing a genuine authenticator and can never be removed themselves:
 * - `password`: the account credential, always present — not an MFA factor.
 * - `email`: frequently auto-attached at signup.
 * - `recovery-code`: auto-generated backup codes, not a factor the user enrolls or manages here.
 */
const HIDDEN_FACTOR_TYPES: ReadonlySet<AuthenticationMethod["type"]> = new Set([
    "password",
    "email",
    "recovery-code",
]);

/** True for a user-managed MFA factor that should appear in the list and count toward the guard. */
export const isManageableFactor = (method: AuthenticationMethod): boolean =>
    !HIDDEN_FACTOR_TYPES.has(method.type);

/** Filters an authentication-method list down to the user-managed MFA factors. */
export const getManageableFactors = (methods: AuthenticationMethod[]): AuthenticationMethod[] =>
    methods.filter(isManageableFactor);

/** Human-readable label for a factor type. */
export const getFactorTypeLabel = (type: AuthenticationMethod["type"]): string => {
    switch (type) {
        case "totp":
            return "Authenticator app";
        case "phone":
            return "Phone";
        case "email":
            return "Email";
        case "passkey":
            return "Passkey";
        case "webauthn-platform":
            return "Device biometrics";
        case "webauthn-roaming":
            return "Security key";
        case "push-notification":
            return "Push notification";
        case "recovery-code":
            return "Recovery code";
        case "password":
            return "Password";
        default:
            return type;
    }
};

/** Masks all but the last 4 characters of a phone number. */
const maskPhone = (phone: string): string =>
    phone.length <= 4 ? phone : `••• ••• ${phone.slice(-4)}`;

/** Identifying detail shown beside the type label (masked phone, email, custom name, etc.). */
export const getFactorDetail = (method: AuthenticationMethod): string | undefined => {
    switch (method.type) {
        case "phone":
            return maskPhone(method.phone_number);
        case "email":
            return method.email;
        case "totp":
        case "webauthn-platform":
        case "webauthn-roaming":
        case "push-notification":
            return method.name;
        default:
            return undefined;
    }
};
